import React from 'react';
import classnames from 'classnames';
import { FaPlus } from 'react-icons/fa';

import Layout from '../../components/Layout';
import SEO from '../../components/seo';
import ChatComposer from '../../components/chat/ChatComposer';
import ChatHelp from '../../components/chat/ChatHelp';
import ChatHistory from '../../components/chat/ChatHistory';
import ChatTranscript from '../../components/chat/ChatTranscript';
import InnaAvatar from '../../components/chat/InnaAvatar';
import useChat from '../../hooks/useChat';
import useResizableSidebar from '../../hooks/useResizableSidebar';

/**
 * Full-page view of INNA. Shares her thread and history with the floating
 * widget through the same local-storage key, so a conversation started in one
 * continues in the other.
 */
const Chat = () => {
    const {
        messages,
        send,
        loading,
        busy,
        conversationId,
        stream,
        pendingConversationId,
        conversations,
        startNew,
        selectConversation,
        deleteConversation,
        clearHistory
    } = useChat();
    const { width: sidebarWidth, dragging, handleProps } = useResizableSidebar();

    return (
        <Layout>
            <SEO title="INNA" />
            <div className="chat-page col-xs-12">
                <div className="chat-page-header between-xs middle-xs">
                    <div className="chat-page-heading">
                        <InnaAvatar size={48} className="mr-3" />
                        <div>
                            <h1 className="mb-0">INNA</h1>
                            <p className="txt-muted mb-0">
                                Your INaudio assistant. I've catalogued every audiobook we ship
                                and listened to precisely none.
                            </p>
                        </div>
                    </div>
                </div>

                <ChatHelp />

                {/* The dragged width reaches both the sidebar and the handle
                    sitting on its edge through one custom property, so the two
                    cannot disagree about where the divider is. */}
                <div
                    className="chat-page-body"
                    style={{ '--chat-sidebar-width': `${sidebarWidth}px` }}
                >
                    <aside className="chat-page-sidebar" aria-label="Conversation history">
                        {/* Heads the sidebar rather than the page: starting a thread
                            and picking an old one are the same decision, so they
                            belong to the same control. */}
                        <button
                            type="button"
                            className="chat-new-conversation"
                            onClick={startNew}
                            disabled={busy}
                        >
                            <FaPlus className="chat-new-conversation-icon" />
                            New convo
                        </button>

                        <h2 className="chat-page-sidebar-heading">History</h2>
                        <ChatHistory
                            conversations={conversations}
                            currentId={conversationId}
                            pendingConversationId={pendingConversationId}
                            onSelect={selectConversation}
                            onDelete={deleteConversation}
                            onClearAll={clearHistory}
                        />
                    </aside>

                    <div
                        {...handleProps}
                        className={classnames('chat-page-resizer', {
                            'chat-page-resizer-active': dragging
                        })}
                    />

                    <div className="chat-page-main">
                        <ChatTranscript messages={messages} loading={loading} stream={stream} onSuggestion={send} />

                        <ChatComposer
                            onSend={send}
                            loading={busy}
                            busyElsewhere={busy && !loading}
                            inputId="chat-page-input"
                        />

                        <p className="chat-page-disclaimer" role="note">
                            <strong>Research and discovery only.</strong> Always verify INNA&apos;s data for accuracy
                            against the relevant source system before using it.
                        </p>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Chat;
